import React, { useEffect, useRef } from 'react';
import { cn } from '../../utils/cn';

/**
 * SLDS 2 tab bar. Scrolls sideways on small screens instead of squashing
 * labels; the active tab is kept in view. Arrow keys move between tabs.
 *
 * tabs: [{ key, label, icon?: LucideIcon, count?: number, hidden?: bool, disabled?: bool, title?: string }]
 */
export default function Tabs({ tabs, value, onChange, className }) {
  const listRef = useRef(null);
  const visible = tabs.filter((t) => !t.hidden);

  useEffect(() => {
    const el = listRef.current?.querySelector('[aria-selected="true"]');
    el?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [value]);

  const onKeyDown = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const enabled = visible.filter((t) => !t.disabled);
    if (!enabled.length) return;
    const i = enabled.findIndex((t) => t.key === value);
    const next = enabled[(i + (e.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length];
    onChange(next.key);
    e.preventDefault();
  };

  return (
    <div ref={listRef} role="tablist" className={cn('slds2-tabs', className)} onKeyDown={onKeyDown}>
      {visible.map(({ key, label, icon: Icon, count, disabled, title }) => {
        const active = key === value;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            disabled={disabled}
            title={title}
            onClick={() => !disabled && onChange(key)}
            className={cn('slds2-tab', active && 'slds2-tab--active', disabled && 'opacity-40 cursor-not-allowed')}
          >
            {Icon && <Icon size={15} />}
            {label}
            {count != null && (
              <span className={cn('ml-0.5 px-1.5 rounded-full text-[11px] tabular-nums', active ? 'bg-[#0176D3]/10' : 'bg-[#F3F3F3]')}>{count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export { Tabs };
